import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  {
    to: "/services",
    label: "Services",
    submenu: [
      { to: "/services", label: "All Services" },
      { to: "/kitchen-renovation-dubai", label: "Kitchen Renovation" },
      { to: "/custom-wardrobes-dubai", label: "Custom Wardrobes" },
      { to: "/custom-joinery-dubai", label: "Custom Joinery" },
      { to: "/custom-sofas-dubai", label: "Custom Sofas" },
      { to: "/villa-renovation-dubai", label: "Villa Renovation" },
    ],
  },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/curtains-dubai", label: "Curtains & Blinds" },
  { to: "/holiday-homes", label: "Holiday Homes" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // desktop hover key
  const [mobileSubOpen, setMobileSubOpen] = useState({}); // mobile expand state
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenSubmenu(null);
  }, [location.pathname]);

  const handleEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenSubmenu(key);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpenSubmenu(null), 120);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF5EC]/95 border-b border-[#6E1F2C]/15 shadow-[0_1px_20px_-8px_rgba(110,31,44,0.2)]"
          : "backdrop-blur-md bg-[#FAF5EC]/80 border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between gap-6 h-20">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="font-serif-display whitespace-nowrap text-xl md:text-2xl tracking-[0.24em] text-[#6E1F2C] uppercase"
        >
          Time <span className="text-[#A3141A]">·</span> Furniture
        </Link>

        <nav className="hidden xl:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => {
            if (n.submenu) {
              const isActiveBranch = n.submenu.some(
                (s) => s.to === location.pathname
              );
              return (
                <div
                  key={n.to}
                  className="relative"
                  onMouseEnter={() => handleEnter(n.to)}
                  onMouseLeave={handleLeave}
                >
                  <NavLink
                    to={n.to}
                    data-testid={`nav-link-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `!inline-flex items-center gap-1 whitespace-nowrap text-[11px] tracking-[0.16em] uppercase transition-colors link-elegant ${
                        isActive || isActiveBranch
                          ? "text-[#6E1F2C]"
                          : "text-[#1C110C]/80 hover:text-[#1C110C]"
                      }`
                    }
                  >
                    {n.label}
                    <ChevronDown
                      size={12}
                      className={`transition-transform duration-200 ${
                        openSubmenu === n.to ? "rotate-180" : ""
                      }`}
                      strokeWidth={2.2}
                    />
                  </NavLink>

                  {openSubmenu === n.to && (
                    <div
                      data-testid="services-submenu"
                      className="absolute left-0 top-full pt-3 min-w-[260px]"
                    >
                      <div className="bg-[#FAF5EC] border border-[#6E1F2C]/15 shadow-[0_8px_30px_-12px_rgba(110,31,44,0.25)] py-3">
                        {n.submenu.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            data-testid={`services-submenu-${sub.to.replace(/\//g, "-")}`}
                            className={({ isActive }) =>
                              `block px-6 py-2.5 text-[11px] tracking-[0.24em] uppercase transition-colors ${
                                isActive
                                  ? "text-[#6E1F2C] bg-[#6E1F2C]/[0.06]"
                                  : "text-[#1C110C]/85 hover:text-[#6E1F2C] hover:bg-[#6E1F2C]/[0.04]"
                              }`
                            }
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap text-[11px] tracking-[0.16em] uppercase transition-colors link-elegant ${
                    isActive ? "text-[#6E1F2C]" : "text-[#1C110C]/80 hover:text-[#1C110C]"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <Link
          to="/contact"
          data-testid="header-cta-quote"
          className="hidden xl:inline-flex items-center whitespace-nowrap bg-[#6E1F2C] text-[#FAF5EC] px-5 py-2.5 text-[11px] tracking-[0.16em] uppercase hover:bg-[#56151F] transition-colors"
        >
          Request Quote
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="xl:hidden text-[#1C110C] p-2 min-w-11 min-h-11 inline-flex items-center justify-center"
          aria-label="Menu"
          aria-expanded={open}
          aria-controls="mobile-navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          id="mobile-navigation"
          className="xl:hidden border-t border-[#6E1F2C]/15 bg-[#FAF5EC]/95 backdrop-blur-xl max-h-[calc(100dvh-80px)] overflow-y-auto overscroll-contain"
        >
          <div className="px-6 py-8 flex flex-col gap-5">
            {NAV.map((n) => {
              if (n.submenu) {
                const isOpenSub = !!mobileSubOpen[n.to];
                return (
                  <div key={n.to} className="flex flex-col">
                    <div className="flex items-center justify-between">
                      <NavLink
                        to={n.to}
                        data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `text-sm tracking-[0.28em] uppercase ${
                            isActive ? "text-[#6E1F2C]" : "text-[#1C110C]"
                          }`
                        }
                      >
                        {n.label}
                      </NavLink>
                      <button
                        type="button"
                        onClick={() =>
                          setMobileSubOpen((s) => ({ ...s, [n.to]: !s[n.to] }))
                        }
                        data-testid="mobile-services-toggle"
                        aria-expanded={isOpenSub}
                        className="p-1 text-[#6E1F2C]"
                        aria-label={`Toggle ${n.label} submenu`}
                      >
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${
                            isOpenSub ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    </div>
                    {isOpenSub && (
                      <div className="mt-4 ml-1 pl-4 border-l border-[#6E1F2C]/20 flex flex-col gap-4">
                        {n.submenu.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            data-testid={`mobile-services-sub-${sub.to.replace(/\//g, "-")}`}
                            className={({ isActive }) =>
                              `text-[11px] tracking-[0.28em] uppercase ${
                                isActive
                                  ? "text-[#6E1F2C]"
                                  : "text-[#1C110C]/80"
                              }`
                            }
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm tracking-[0.28em] uppercase ${
                      isActive ? "text-[#6E1F2C]" : "text-[#1C110C]"
                    }`
                  }
                  end={n.to === "/"}
                >
                  {n.label}
                </NavLink>
              );
            })}
            <Link
              to="/contact"
              data-testid="mobile-cta-quote"
              className="mt-2 inline-block bg-[#6E1F2C] text-[#FAF5EC] px-6 py-4 text-[11px] tracking-[0.28em] uppercase text-center"
            >
              Request Quote
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
